import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { date, fine, money, testingWords, weight } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { PrintLetterhead, PrintSignatures, PrintToolbar } from '../../components/print/PrintDocument';

interface Melt {
  doc_no: string;
  status: string;
  melt_date: string;
  received_date: string | null;
  branch_name: string;
  metal_name: string;
  refiner_name: string | null;
  melter_name: string | null;
  input_gross_wt: string;
  input_fine_wt: string;
  input_cost: string;
  output_wt: string | null;
  output_fine_wt: string | null;
  sample_wt: string;
  sample_fine_wt: string;
  recovered_wt: string;
  recovered_fine_wt: string;
  recovered_purity_code: string | null;
  loss_fine_wt: string | null;
  loss_pct: string | null;
  allowed_loss_pct: string;
  excess_loss_value: string;
  expected_return_date: string | null;
  charges_fine_wt: string;
  recovered_amount: string;
  recovery_rate: string | null;
  charges: string;
  charges_tax: string;
  refiner_bill_no: string | null;
  testing_method: string | null;
  assay_cert_no: string | null;
  assayed_by: string | null;
  remarks: string | null;
  requested_by_name: string | null;
  received_by_name: string | null;
  approved_by_name: string | null;
  lines: {
    line_no: number;
    item_code: string | null;
    lot_code: string | null;
    description: string | null;
    product_name: string | null;
    purity_code: string;
    gross_wt: string;
    fine_wt: string;
    cost_amount: string;
  }[];
  bars: {
    line_no: number;
    bar_no: string;
    purity_code: string;
    weight: string;
    fine_wt: string;
    assay_pct: string | null;
    hallmark_uid: string | null;
    cost_amount: string | null;
  }[];
}

const STATUS_WORD: Record<string, string> = {
  IN_PROCESS: 'Issued — out for melting',
  PENDING_APPROVAL: 'Received — loss awaiting approval',
  POSTED: 'Completed',
  CANCELLED: 'Cancelled',
};

/**
 * The melting slip: what was handed to the melter, signed for when it left,
 * and — once the bars are back — what returned, the assay, and the loss. The
 * same sheet serves as the issue voucher and the settlement.
 */
export function MeltingPrintPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, error, isLoading } = useQuery({
    queryKey: ['melting', id],
    queryFn: () => api.get<Melt>(`/meltings/${id}`),
  });
  const { header } = usePrintHeader(data?.branch_name);

  if (isLoading) return <Loading what="Preparing the slip" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return null;

  const m = data;
  const melter = m.refiner_name ?? m.melter_name ?? 'In-house';
  const back = !!m.output_fine_wt;
  const barsCost = m.bars.reduce((a, b) => a + Number(b.cost_amount ?? 0), 0);

  return (
    <>
      <PrintToolbar onBack={() => navigate(`/melting?open=${id}`)} fileName={`${m.doc_no} melting slip`} />
      <div className="print-sheet text-sm">
        <PrintLetterhead
          header={header}
          title="Melting slip"
          docNo={m.doc_no}
          docDate={date(m.melt_date)}
          status={STATUS_WORD[m.status] ?? m.status}
        />

        <div className="mb-5 grid grid-cols-3 gap-4 rounded border border-ink/15 px-3 py-2">
          <div>
            <p className="doc-label">Melter / refiner</p>
            <p className="font-medium">{melter}</p>
            <p className="text-xs">{m.metal_name}</p>
          </div>
          <div>
            <p className="doc-label">Sent</p>
            <p className="num">{date(m.melt_date)}</p>
            <p className="text-xs">by {m.requested_by_name ?? '—'}</p>
          </div>
          <div>
            <p className="doc-label">Received</p>
            <p className="num">{m.received_date ? date(m.received_date) : '—'}</p>
            {m.received_by_name ? (
              <p className="text-xs">by {m.received_by_name}</p>
            ) : m.expected_return_date ? (
              <p className="text-xs">due by {date(m.expected_return_date)}</p>
            ) : null}
          </div>
        </div>

        <h3 className="mb-1 mt-2 text-sm font-semibold">Issued for melting</h3>
        <table className="doc-table mb-5">
          <thead>
            <tr>
              <th>#</th>
              <th>Tag / lot</th>
              <th>Description</th>
              <th className="c">Purity</th>
              <th className="r">Gross</th>
              <th className="r">Fine</th>
            </tr>
          </thead>
          <tbody>
            {m.lines.map((l) => (
              <tr key={l.line_no}>
                <td className="num !text-left">{l.line_no}</td>
                <td className="num !text-left">{l.item_code ?? l.lot_code ?? l.description}</td>
                <td>{l.product_name ?? '—'}</td>
                <td className="c num">{l.purity_code}</td>
                <td className="num">{weight(l.gross_wt)}</td>
                <td className="num">{fine(l.fine_wt)}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="!border-b-0 !border-t !border-ink/40" colSpan={4}>
                Total issued
              </td>
              <td className="num !border-b-0 !border-t !border-ink/40">{weight(m.input_gross_wt)}</td>
              <td className="num !border-b-0 !border-t !border-ink/40">{fine(m.input_fine_wt)}</td>
            </tr>
          </tbody>
        </table>

        {back ? (
          <>
            <h3 className="mb-1 mt-2 text-sm font-semibold">Received back</h3>
            <table className="doc-table mb-5">
              <thead>
                <tr>
                  <th>Bar</th>
                  <th className="c">Purity</th>
                  <th className="r">Assay %</th>
                  <th>HUID</th>
                  <th className="r">Weight</th>
                  <th className="r">Fine</th>
                </tr>
              </thead>
              <tbody>
                {m.bars.map((b) => (
                  <tr key={b.line_no}>
                    <td className="num !text-left">{b.bar_no}</td>
                    <td className="c num">{b.purity_code}</td>
                    <td className="num">{b.assay_pct ? Number(b.assay_pct).toFixed(2) : '—'}</td>
                    <td className="num !text-left">{b.hallmark_uid ?? '—'}</td>
                    <td className="num">{weight(b.weight)}</td>
                    <td className="num">{fine(b.fine_wt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="keep-together grid grid-cols-2 items-start gap-8 text-[13px]">
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="py-0.5">Fine issued</td>
                    <td className="num py-0.5 text-right">{fine(m.input_fine_wt)}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Fine in bars</td>
                    <td className="num py-0.5 text-right">{fine(m.output_fine_wt)}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Assay sample ({weight(m.sample_wt)})</td>
                    <td className="num py-0.5 text-right">{fine(m.sample_fine_wt)}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">
                      Dust recovered ({weight(m.recovered_wt)}
                      {m.recovered_purity_code ? ` @ ${m.recovered_purity_code}` : ''})
                    </td>
                    <td className="num py-0.5 text-right">{fine(m.recovered_fine_wt)}</td>
                  </tr>
                  {Number(m.charges_fine_wt) > 0 ? (
                    <tr>
                      <td className="py-0.5">Fee kept in metal</td>
                      <td className="num py-0.5 text-right">{fine(m.charges_fine_wt)}</td>
                    </tr>
                  ) : null}
                  <tr className="border-t border-ink font-semibold">
                    <td className="py-1">Melting loss</td>
                    <td className="num py-1 text-right">
                      {fine(m.loss_fine_wt)} ({Number(m.loss_pct ?? 0).toFixed(2)}%)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-xs text-ink-soft">Allowed</td>
                    <td className="num py-0.5 text-right text-xs text-ink-soft">
                      {Number(m.allowed_loss_pct).toFixed(2)}%
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="py-0.5">Assay</td>
                    <td className="py-0.5 text-right">
                      {m.testing_method ? testingWords(m.testing_method).replace(/^./, (c) => c.toUpperCase()) : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Certificate</td>
                    <td className="num py-0.5 text-right">{m.assay_cert_no ?? '—'}</td>
                  </tr>
                  {m.assayed_by ? (
                    <tr>
                      <td className="py-0.5">Assayed by</td>
                      <td className="py-0.5 text-right">{m.assayed_by}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td className="py-0.5">Charges{m.refiner_bill_no ? ` (bill ${m.refiner_bill_no})` : ''}</td>
                    <td className="num py-0.5 text-right">{money(m.charges)}</td>
                  </tr>
                  {Number(m.charges_tax) > 0 ? (
                    <tr>
                      <td className="py-0.5">GST on charges</td>
                      <td className="num py-0.5 text-right">{money(m.charges_tax)}</td>
                    </tr>
                  ) : null}
                  {Number(m.excess_loss_value) > 0 ? (
                    <tr>
                      <td className="py-0.5">
                        {Number(m.recovered_amount) > 0 ? 'Excess loss, at cost' : 'Excess loss written off'}
                      </td>
                      <td className="num py-0.5 text-right">{money(m.excess_loss_value)}</td>
                    </tr>
                  ) : null}
                  {Number(m.recovered_amount) > 0 ? (
                    <tr className="font-medium">
                      <td className="py-0.5">
                        Charged to {melter}
                        {m.recovery_rate ? (
                          <span className="block text-[11px] text-ink-soft">
                            excess loss at {money(m.recovery_rate)} a fine gram
                          </span>
                        ) : null}
                      </td>
                      <td className="num py-0.5 text-right">{money(m.recovered_amount)}</td>
                    </tr>
                  ) : null}
                  {barsCost > 0 ? (
                    <tr className="border-t border-ink font-semibold">
                      <td className="py-1">Cost of bars</td>
                      <td className="num py-1 text-right">{money(barsCost.toFixed(2))}</td>
                    </tr>
                  ) : null}
                  {m.approved_by_name ? (
                    <tr>
                      <td className="py-0.5 text-xs text-ink-soft">Loss approved by</td>
                      <td className="py-0.5 text-right text-xs text-ink-soft">{m.approved_by_name}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="mb-4 rounded border border-ink/20 p-2 text-xs">
            Fine metal of <span className="num font-semibold">{fine(m.input_fine_wt)}</span> handed over for melting on{' '}
            {date(m.melt_date)}
            {m.expected_return_date ? (
              <>
                , to be returned by <span className="font-semibold">{date(m.expected_return_date)}</span>
              </>
            ) : null}
            . Bars, assay sample and any dust are to be returned against this slip, and loss above{' '}
            {Number(m.allowed_loss_pct).toFixed(2)}% accounted for.
          </p>
        )}

        {m.remarks ? <p className="mt-3 text-xs text-ink-soft">Remarks: {m.remarks}</p> : null}

        <PrintSignatures
          left={`${back ? 'Returned by' : 'Received by'} (${melter})`}
          right={`For ${header.companyName}`}
        />
      </div>
    </>
  );
}
